import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { appsAPI } from '../api/appsAPI';
import { quizGeneratorAPI } from '../../quiz-generation/api/quizGeneratorAPI';
import QuizDisplaySection from '../../quiz-generation/components/QuizDisplaySection';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { scrollDocumentToTop, useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { resolveAppLaunchTarget, navigateAppLaunch, isStandaloneReasoningApp } from '../utils/appDisplayUtils';
import '../../quiz-generation/components/QuizGeneratorPage.css';
import './AppDetailPage.css';

const INTERVIEW_EXAM_NAMES = [
  'medicine_mmi_interview',
  'medicine_panel_interview',
  'oxbridge_interview',
  'interview_simulator',
  'investment_banking_interview',
  'private_equity_interview',
  'engineering_interview',
  'computer_science_interview',
  'law_interview',
  'operations_fs_interview',
  'scholarship_interview',
];

const AppTestsPage = ({ appId }) => {
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  
  // Quiz state for inline quiz display
  const [currentQuiz, setCurrentQuiz] = useState(null);
  useScrollDocumentToTop(currentQuiz ? 'quiz' : 'list');
  const [userAnswers, setUserAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [testTimer, setTestTimer] = useState(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false);
  const quizStartedAt = useRef(null);
  const testTimerRef = useRef(null);

  useEffect(() => {
    testTimerRef.current = testTimer;
  }, [testTimer]);

  // Fetch app details and quizzes
  useEffect(() => {
    const fetchApp = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(appId);

        // Redirect if app is not owned and not unlocked (detail page can purchase/add)
        if (appData && appData.is_owned === false && appData.is_unlocked !== true) {
          navigate(`/apps-store/${appId}`, { replace: true });
          return;
        }

        // If this is one of the dedicated interview tools, redirect straight to the interview page
        const examName = (appData.exam_name || '').trim().toLowerCase();
        if (
          appData.app_type === 'tutorial' &&
          INTERVIEW_EXAM_NAMES.includes(examName)
        ) {
          navigate(`/website-interview?toolType=${encodeURIComponent(appData.exam_name)}`);
          return;
        }

        // If this is an admissions exam, redirect to the specialized grid practice portal
        const ADMISSIONS_EXAMS = ['lnat', 'tmua', 'ucat', 'lsat'];
        if (
          appData.app_type === 'tutorial' &&
          ADMISSIONS_EXAMS.includes(examName)
        ) {
          navigate(`/practice/admissions/${examName}`);
          return;
        }

        // Standalone reasoning apps → /website-reasoning-test?type= (never old experience suite)
        if (isStandaloneReasoningApp(appData)) {
          const target = resolveAppLaunchTarget(appData);
          if (target) {
            navigateAppLaunch(navigate, target, { replace: true });
            return;
          }
        }

        setApp(appData);
        
        // Fetch quizzes for tutorial apps that actually have tests
        if (appData.app_type === 'tutorial') {
          const reasoningOnly = isStandaloneReasoningApp(appData) || (
            !appData.case_study_id && (
              appData.has_logical_reasoning ||
              appData.has_pattern_recognition ||
              appData.has_numerical_reasoning ||
              appData.has_verbal_reasoning
            )
          );
          if (!reasoningOnly) {
            setLoadingQuizzes(true);
            try {
              const quizzesData = await appsAPI.getAppQuizzes(appId);
              setQuizzes(quizzesData.quizzes || []);
            } catch (error) {
              console.error('Error fetching quizzes:', error);
            } finally {
              setLoadingQuizzes(false);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching app:', error);
      } finally {
        setLoading(false);
      }
    };

    if (appId) {
      fetchApp();
    }
  }, [appId, navigate]);

  const startTimer = (duration) => {
    setTimeRemaining(duration * 60);
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          alert('Time is up!');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTestTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (testTimer) {
        clearInterval(testTimer);
      }
    };
  }, [testTimer]);

  const handleStartTest = async (test) => {
    try {
      setIsLoadingQuiz(true);
      let result;
      
      if (test.paper_id) {
        result = await quizGeneratorAPI.startSetPaper(test.exam_name, test.paper_id);
      } else {
        result = await quizGeneratorAPI.startExam(test.exam_name);
      }
      
      if (result.success && result.questions) {
        setCurrentQuiz(result.questions);
        quizStartedAt.current = Date.now();
        if (result.exam_config && result.exam_config.duration) {
          startTimer(result.exam_config.duration);
        }
        scrollDocumentToTop();
      }
    } catch (error) {
      console.error('Error starting test:', error);
      alert('Failed to start test: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoadingQuiz(false);
    }
  };

  const handleQuizBack = useCallback(() => {
    setCurrentQuiz(null);
    setUserAnswers({});
    setTimeRemaining(0);
    if (testTimerRef.current) {
      clearInterval(testTimerRef.current);
      setTestTimer(null);
    }
    scrollDocumentToTop();
  }, []);

  const handleQuizSubmit = async () => {};

  const backToApps = useCallback(() => {
    navigate('/apps');
    scrollDocumentToTop();
  }, [navigate]);

  // In-test → tests list; tests list → Apps (via shared floating back)
  const handleProductBack = useCallback(() => {
    if (currentQuiz) {
      handleQuizBack();
      return;
    }
    backToApps();
  }, [currentQuiz, handleQuizBack, backToApps]);

  useProductBackHandler(handleProductBack);

  if (loading) {
    return (
      <div className="app-detail-page netflix-style" style={{ padding: '48px 24px' }}>
        <div className="apps-loading-spinner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div className="spinner-dot" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="app-detail-page netflix-style">
        <div className="app-detail-error">
          <p>App not found</p>
          <button onClick={backToApps} className="btn-back-to-store">
            Back to Apps
          </button>
        </div>
      </div>
    );
  }

  if (currentQuiz) {
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
      <div className="app-detail-page netflix-style">
        {timeRemaining > 0 && (
          <div
            className={`app-detail-timer product-tests-timer ${timeRemaining < 300 ? 'warning' : ''}`}
            style={{
              position: 'fixed',
              top: 'max(20px, env(safe-area-inset-top, 0px))',
              right: 'max(20px, env(safe-area-inset-right, 0px))',
              zIndex: 10050,
            }}
          >
            Time: {formatTime(timeRemaining)}
          </div>
        )}

        <main className="product-tests-quiz-main" style={{ padding: '72px 0 24px', minHeight: '100vh', background: 'var(--iv-bg)' }}>
          <div id="quiz-display-section">
            <QuizDisplaySection
              questions={currentQuiz}
              userAnswers={userAnswers}
              setUserAnswers={setUserAnswers}
              onSubmit={handleQuizSubmit}
              onBack={handleQuizBack}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-detail-page netflix-style productivity-tests-page">
      <main className="app-detail-main" style={{ paddingTop: '72px' }}>
        <div className="app-detail-content">
          <div className="app-detail-left-column">
            <section className="app-detail-section">
              <h2 className="app-detail-section-title">{app.title} - Available Tests</h2>
              {loadingQuizzes ? null : quizzes.length > 0 ? (
                <div className="app-detail-quizzes-list">
                  {quizzes.map((test) => {
                    const testId = test.paper_id || test.id;
                    const isSetPaper = !!test.paper_id;
                    
                    return (
                      <div 
                        key={testId} 
                        className="app-detail-quiz-card"
                      >
                        <div className="app-detail-quiz-info">
                          <h3 className="app-detail-quiz-title">{test.title || test.display_name}</h3>
                          <p className="app-detail-quiz-meta">
                            {test.question_count} questions • {test.exam_name}
                            {isSetPaper && <span className="app-detail-paper-badge">Past Paper</span>}
                          </p>
                        </div>
                        <button 
                          className="app-detail-quiz-button"
                          onClick={() => handleStartTest(test)}
                          disabled={isLoadingQuiz}
                        >
                          {isLoadingQuiz ? 'Loading...' : 'Start Test'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="app-detail-no-quizzes">
                  <p>No tests available for this exam.</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppTestsPage;
